import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { cn } from "@/utils/helpers";

const TABBABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** The elements Tab can actually land on inside the panel, in order. */
function tabbables(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) =>
      el.tabIndex >= 0 &&
      !(el as HTMLButtonElement).disabled &&
      el.offsetParent !== null,
  );
}

/**
 * The dialog contract, for any panel that is mounted only while open: focus
 * moves in on mount (the first tabbable control, else the panel), Tab and
 * Shift+Tab wrap inside it, Escape closes unless it was spent closing an open
 * listbox inside, and focus returns to whatever held it before, on unmount.
 * SignalsDialog and the phone's SourceSheet both run on it.
 */
export function useDialogTrap(
  panelRef: RefObject<HTMLElement | null>,
  onClose: () => void,
) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const previous = document.activeElement as HTMLElement | null;
    (tabbables(panel)[0] ?? panel).focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // An open listbox (the kind picker) owns its own Escape.
        const target = e.target as HTMLElement | null;
        if (target?.closest('[aria-expanded="true"]')) return;
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = tabbables(panel);
      if (focusable.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!panel.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      if (previous && previous.isConnected && typeof previous.focus === "function") {
        previous.focus();
      }
    };
  }, [panelRef]);
}

/**
 * The frame both Signals dialogs share: a scrim, one panel at radius 16 (pegs
 * are retired with style B), and the dialog contract.
 *
 *   role="dialog" + aria-modal on the panel, named by `labelledBy`.
 *   Focus moves in on mount (the first tabbable control, else the panel) and is
 *   trapped: Tab and Shift+Tab wrap inside the panel.
 *   Escape closes, unless it was spent closing an open listbox inside.
 *   Focus returns to whatever held it before, on unmount.
 *
 * It is mounted only while open, so every one of those effects is tied to the
 * dialog's own lifetime rather than to an `isOpen` flag on a hidden copy.
 * Rendered at the board root, never inside a card: an ancestor with a transform
 * would otherwise become the fixed panel's containing block.
 */
export default function SignalsDialog({
  labelledBy,
  onClose,
  className,
  children,
}: {
  labelledBy: string;
  onClose: () => void;
  /** Size variant on the panel ("sig-dialog__panel--new" / "--sources"). */
  className?: string;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  useDialogTrap(panelRef, onClose);

  return (
    <div className="sig-dialog">
      <div className="sig-dialog__scrim" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        className={cn("sig-dialog__panel", className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}
